import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalize } from '../utils/payloadFormat';
import { countDifferences, DiffRow, sideBySideDiff } from '../utils/payloadDiff';

interface CompareViewProps {
  leftLabel: string;
  rightLabel: string;
  left?: string | null;
  right?: string | null;
}

const ROW_HEIGHT = 22;
const VIEWPORT_HEIGHT = 480;
const OVERSCAN = 8;

const changeStyle: Record<DiffRow['change'], string> = {
  unchanged: '',
  added: 'bg-success-subtle',
  removed: 'bg-danger-subtle',
  changed: 'bg-warning-subtle',
};

const CompareView: React.FC<CompareViewProps> = ({ leftLabel, rightLabel, left, right }) => {
  const { t } = useTranslation();
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);

  const allRows = useMemo(() => sideBySideDiff(normalize(left), normalize(right)), [left, right]);
  const diffCount = useMemo(() => countDifferences(allRows), [allRows]);

  const rows = useMemo(
    () => (onlyDiff ? allRows.filter((r) => r.change !== 'unchanged') : allRows),
    [allRows, onlyDiff]
  );

  // Lightweight virtualization so large payloads don't render thousands of DOM rows.
  const totalHeight = rows.length * ROW_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(rows.length, Math.ceil((scrollTop + VIEWPORT_HEIGHT) / ROW_HEIGHT) + OVERSCAN);
  const visible = rows.slice(startIndex, endIndex);

  const renderCell = (num?: number, text?: string) => (
    <div className="d-flex" style={{ width: '50%', overflow: 'hidden' }}>
      <span className="text-muted text-end pe-2 flex-shrink-0" style={{ width: 48, userSelect: 'none' }}>
        {num ?? ''}
      </span>
      <pre className="mb-0 flex-grow-1" style={{ whiteSpace: 'pre', overflow: 'hidden' }}>
        {text ?? ''}
      </pre>
    </div>
  );

  return (
    <div className="gg-compare-view">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="small text-muted">
          {diffCount === 0 ? (
            <span className="text-success">{t('NoDifferences')}</span>
          ) : (
            <span>{t('DifferencesCount', { count: diffCount })}</span>
          )}
        </div>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="onlyDiffToggle"
            checked={onlyDiff}
            onChange={(e) => setOnlyDiff(e.target.checked)}
          />
          <label className="form-check-label small" htmlFor="onlyDiffToggle">
            {t('ShowOnlyDifferences')}
          </label>
        </div>
      </div>

      <div className="d-flex border-bottom fw-bold small bg-light">
        <div style={{ width: '50%' }} className="ps-5 py-1">
          {leftLabel}
        </div>
        <div style={{ width: '50%' }} className="ps-5 py-1">
          {rightLabel}
        </div>
      </div>

      <div
        className="border font-monospace"
        style={{ height: VIEWPORT_HEIGHT, overflow: 'auto', fontSize: '0.8rem' }}
        onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
      >
        <div style={{ height: totalHeight, position: 'relative' }}>
          <div style={{ position: 'absolute', top: startIndex * ROW_HEIGHT, left: 0, right: 0 }}>
            {visible.map((row, i) => (
              <div
                key={startIndex + i}
                className="d-flex"
                style={{ height: ROW_HEIGHT, lineHeight: `${ROW_HEIGHT}px` }}
              >
                <div className={`d-flex ${changeStyle[row.change]}`} style={{ width: '100%' }}>
                  {renderCell(row.leftNumber, row.left)}
                  {renderCell(row.rightNumber, row.right)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareView;
